import { useEffect } from "react";
import { isNoindexLocation } from "@/data/noindexLocations";
import { BUSINESS_DESCRIPTION, LOGO_URL, BUSINESS_INFO } from "@/data/businessInfo";
import { primaryTownPath, PRIMARY_TOWN_PATHS } from "@/data/seoTownPrimary";
import { canonicalPathname } from "@/lib/canonicalPath";
import { canonicalPageUrl, normalizeWebsiteUrl, normalizeSchemaWebsiteUrls } from "@/lib/siteIdentity";

interface ServiceSchema {
  name: string;
  description: string;
  provider?: string;
  areaServed?: string;
  image?: string;
  priceRange?: string;
  serviceType?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface ReviewSchema {
  author: string;
  reviewBody: string;
  ratingValue: number;
  datePublished?: string;
}

interface ArticleImageObject {
  url: string;
  caption?: string;
  width?: number;
  height?: number;
}

interface ArticleSchema {
  headline: string;
  description: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  images?: ArticleImageObject[];
}

interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

interface HowToSchema {
  name: string;
  description: string;
  totalTime?: string;
  estimatedCost?: { currency: string; value: string };
  supply?: string[];
  tool?: string[];
  steps: HowToStep[];
}

interface BreadcrumbEntry {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  ogTitle?: string;
  ogType?: 'website' | 'article';
  ogDescription?: string;
  breadcrumbItems?: BreadcrumbEntry[];
  extraSchemas?: object[];
  canonicalUrl?: string;
  townName?: string;
  countyName?: string;
  ogImage?: string;
  ogImageAlt?: string;
  serviceSchema?: ServiceSchema;
  faqItems?: FAQItem[];
  reviews?: ReviewSchema[];
  articleSchema?: ArticleSchema;
  howToSchema?: HowToSchema;
  localBusinessSchema?: Record<string, unknown>;
  isHomePage?: boolean;
  isServicePage?: boolean;
  isBlogPost?: boolean;
  includeArticleSchema?: boolean;
  isTownPage?: boolean;
  serviceName?: string; // e.g., "Interior Painting", "Deck Staining"
  noIndex?: boolean; // private/utility pages (booking management, admin, 404)
}

const SEOHead = ({
  title,
  description,
  ogTitle,
  ogType = 'website',
  ogDescription,
  breadcrumbItems,
  extraSchemas,
  canonicalUrl,
  townName = "Wayne",
  countyName,
  ogImage = "https://alltimepaintandplaster.com/og-image.jpg",
  ogImageAlt,
  serviceSchema,
  faqItems,
  reviews,
  articleSchema,
  howToSchema,
  localBusinessSchema,
  isHomePage = false,
  isServicePage = false,
  isBlogPost = false,
  includeArticleSchema = false,
  isTownPage = false,
  serviceName,
  noIndex = false,
}: SEOHeadProps) => {
  useEffect(() => {
    // Set document title
    document.title = title;

    // Helper to set/update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    setMetaTag("description", description);
    const currentPath = canonicalPathname(window.location.pathname);
    const effectiveCanonical = PRIMARY_TOWN_PATHS[currentPath]
      ? `${BUSINESS_INFO.website}${primaryTownPath(currentPath)}`
      : canonicalPageUrl(currentPath);
    setMetaTag("robots", noIndex ? "noindex, nofollow" : isNoindexLocation(currentPath) || PRIMARY_TOWN_PATHS[currentPath] ? "noindex, follow" : "index, follow");

    // Open Graph tags
    const socialTitle = ogTitle || title;
    const socialDescription = ogDescription || description;
    setMetaTag("og:title", socialTitle, true);
    setMetaTag("og:description", socialDescription, true);
    setMetaTag("og:type", ogType, true);
    setMetaTag("og:image", normalizeWebsiteUrl(ogImage), true);
    setMetaTag("og:image:width", "1200", true);
    setMetaTag("og:image:height", "630", true);
    if (ogImageAlt) {
      setMetaTag("og:image:alt", ogImageAlt, true);
    }
    setMetaTag("og:site_name", BUSINESS_INFO.name, true);
    if (effectiveCanonical) {
      setMetaTag("og:url", effectiveCanonical, true);
    }

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", socialTitle);
    setMetaTag("twitter:description", socialDescription);
    setMetaTag("twitter:image", normalizeWebsiteUrl(ogImage));
    if (ogImageAlt) {
      setMetaTag("twitter:image:alt", ogImageAlt);
    }

    // Set canonical URL
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (effectiveCanonical) {
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = effectiveCanonical;
    }

    // WebSite schema (for homepage)
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${BUSINESS_INFO.website}/#website`,
      "url": BUSINESS_INFO.website,
      "name": BUSINESS_INFO.name,
      "description": BUSINESS_DESCRIPTION,
      "publisher": {
        "@id": `${BUSINESS_INFO.website}/#organization`
      },
    };

    // Enhanced LocalBusiness structured data
    const baseLocalBusinessSchema = {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "HomeAndConstructionBusiness", "HousePainter"],
      "@id": `${BUSINESS_INFO.website}/#organization`,
      "name": BUSINESS_INFO.name,
      "description": BUSINESS_DESCRIPTION,
      "url": BUSINESS_INFO.website,
      "telephone": BUSINESS_INFO.phone,
      "email": BUSINESS_INFO.email,
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "telephone": "+1-201-701-8248",
        "email": BUSINESS_INFO.email,
        "areaServed": "US-NJ",
        "availableLanguage": "English"
      },
      "image": [
        `${BUSINESS_INFO.website}/og-image.jpg`,
        `${BUSINESS_INFO.website}/hero-bg-optimized.webp`
      ],
      "logo": LOGO_URL,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": BUSINESS_INFO.address.street,
        "addressLocality": BUSINESS_INFO.address.city,
        "addressRegion": BUSINESS_INFO.address.state,
        "postalCode": BUSINESS_INFO.address.zip,
        "addressCountry": BUSINESS_INFO.address.country
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": BUSINESS_INFO.geo.latitude,
        "longitude": BUSINESS_INFO.geo.longitude
      },
      "areaServed": countyName ? [
        {
          "@type": "City",
          "name": townName,
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": countyName
          }
        },
        {
          "@type": "AdministrativeArea",
          "name": countyName
        },
        {
          "@type": "State",
          "name": BUSINESS_INFO.address.stateFullName
        }
      ] : [
        ...BUSINESS_INFO.serviceAreas.map(area => ({
          "@type": "AdministrativeArea" as const,
          "name": area
        })),
        {
          "@type": "State" as const,
          "name": BUSINESS_INFO.address.stateFullName
        }
      ],
      "priceRange": BUSINESS_INFO.priceRange,
      "currenciesAccepted": "USD",
      "paymentAccepted": ["Cash", "Credit Card", "Check", "Bank Transfer"],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "08:00",
          "closes": "20:00"
        }
      ],
      "sameAs": [
        BUSINESS_INFO.social.facebook,
        BUSINESS_INFO.social.instagram,
        BUSINESS_INFO.social.tiktok,
        BUSINESS_INFO.social.youtube
      ],
      // NJ Home Improvement Contractor License credential
      "hasCredential": {
        "@type": "GovernmentPermit",
        "name": BUSINESS_INFO.license.type,
        "issuedBy": {
          "@type": "GovernmentOrganization",
          "name": BUSINESS_INFO.license.issuedBy
        },
        "validIn": {
          "@type": "State",
          "name": BUSINESS_INFO.address.stateFullName
        },
        "identifier": BUSINESS_INFO.license.number
      },
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "NJ HIC License",
          "value": BUSINESS_INFO.license.number
        },
        {
          "@type": "PropertyValue",
          "name": "Insurance",
          "value": "Insured"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Architectural Finishes & Painting Services",
        "itemListElement": [
          {
            "@type": "OfferCatalog",
            "name": "Decorative Plaster & Mineral Finishes",
            "itemListElement": ["Venetian Plaster", "Decorative Lime Plaster", "Limewash", "Microcement", "Plaster Fireplace Finishes"].map(name => ({
              "@type": "Offer",
              "itemOffered": { "@type": "Service", "name": name }
            }))
          },
          {
            "@type": "OfferCatalog",
            "name": "Residential Painting",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Interior Painting",
                  "description": "Professional interior painting for walls, ceilings, trim, and accent walls"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Exterior Painting",
                  "description": "Weather-resistant exterior painting for siding, trim, and fascia"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cabinet Refinishing",
                  "description": "Factory-quality spray finishing for kitchen and bathroom cabinets"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Deck Staining",
                  "description": "Professional deck staining for IPE, cedar, and pressure-treated wood"
                }
              }
            ]
          },
          {
            "@type": "OfferCatalog",
            "name": "Commercial Painting",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Commercial Interior Painting",
                  "description": "Professional painting for offices, retail, and commercial spaces"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Industrial Painting",
                  "description": "Specialized coatings for industrial facilities"
                }
              }
            ]
          }
        ]
      },
      "knowsAbout": [
        "Venetian Plaster",
        "Decorative Lime Plaster",
        "Limewash",
        "Microcement",
        "Architectural Fireplace Finishes",
        "Interior House Painting",
        "Exterior House Painting",
        "Cabinet Painting",
        "Cabinet Refinishing",
        "Deck Staining",
        "Commercial Painting",
        "Wallpaper Installation",
        "Color Consultation"
      ]
    };

    // Allow per-page LocalBusiness customization (e.g., service-area pages)
    const mergedLocalBusinessSchema = localBusinessSchema
      ? { ...baseLocalBusinessSchema, ...localBusinessSchema }
      : baseLocalBusinessSchema;

    // Enhanced Service schema for service pages
    const serviceSchemaData = serviceSchema ? {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${effectiveCanonical}#service`,
      "name": serviceSchema.name,
      "description": serviceSchema.description,
      "provider": { "@id": `${BUSINESS_INFO.website}/#organization`, "name": BUSINESS_INFO.name },
      "areaServed": {
        "@type": "State",
        "name": BUSINESS_INFO.address.stateFullName
      },
      "serviceType": serviceSchema.serviceType || serviceSchema.name,
      "category": "Home Improvement",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `${serviceSchema.name} Services`,
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": serviceSchema.name
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "priceCurrency": "USD"
            },
            "eligibleRegion": {
              "@type": "State",
              "name": BUSINESS_INFO.address.stateFullName
            }
          }
        ]
      },
      "termsOfService": `${BUSINESS_INFO.website}/terms-of-service/`,
      ...(serviceSchema.image && { "image": serviceSchema.image })
    } : null;

    // BreadcrumbList schema for service pages and town pages
    const breadcrumbName = serviceSchema?.name || serviceName || "Painting Services";
    const customBreadcrumbSchema = breadcrumbItems && breadcrumbItems.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": normalizeWebsiteUrl(crumb.url)
      }))
    } : null;
    const breadcrumbSchema = customBreadcrumbSchema ? null : (isServicePage || isTownPage) && (serviceSchema || serviceName) && canonicalUrl ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": isTownPage ? [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": BUSINESS_INFO.website
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": serviceName || "Painting Services",
          "item": `${BUSINESS_INFO.website}/services`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": `${serviceName || "Painting"} in ${townName}`,
          "item": effectiveCanonical
        }
      ] : [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": BUSINESS_INFO.website
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": `${BUSINESS_INFO.website}/services`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": breadcrumbName,
          "item": effectiveCanonical
        }
      ]
    } : null;

    // Town pages: a Service offered in that town by the single business entity
    // (never a second LocalBusiness with a made-up local address).
    const townLocalBusinessSchema = isTownPage ? {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${effectiveCanonical}#service`,
      "name": serviceName ? `${serviceName} in ${townName}, NJ` : `Painting Services in ${townName}, NJ`,
      "description": serviceSchema?.description || `${serviceName || "Painting"} services in ${townName}, NJ by ${BUSINESS_INFO.name}, based in Wayne, New Jersey.`,
      "serviceType": serviceName || "Painting Services",
      "url": effectiveCanonical,
      "provider": { "@id": `${BUSINESS_INFO.website}/#organization`, "name": BUSINESS_INFO.name },
      "areaServed": {
        "@type": "City",
        "name": townName,
        "containedInPlace": countyName
          ? { "@type": "AdministrativeArea", "name": countyName }
          : { "@type": "State", "name": BUSINESS_INFO.address.stateFullName }
      }
    } : null;

    // FAQ schema for rich snippets
    const faqSchemaData = faqItems && faqItems.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    } : null;

    // Individual Review schemas
    const reviewSchemas = reviews && reviews.length > 0 ? reviews.map(review => ({
      "@context": "https://schema.org",
      "@type": "Review",
      "itemReviewed": {
        "@id": `${BUSINESS_INFO.website}/#organization`,
        "name": BUSINESS_INFO.name
      },
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "reviewBody": review.reviewBody,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.ratingValue,
        "bestRating": 5,
        "worstRating": 1
      },
      ...(review.datePublished && { "datePublished": review.datePublished })
    })) : [];

    // ItemList schema with aggregate rating for SERP features (homepage)
    const itemListWithRatingSchema = isHomePage && reviews && reviews.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `${BUSINESS_INFO.name} Customer Reviews`,
      "description": `Customer reviews and ratings for ${BUSINESS_INFO.name} painting services in Northern New Jersey`,
      "numberOfItems": reviews.length,
      "itemListElement": reviews.map((review, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": `Review by ${review.author}`,
        "item": {
          "@type": "Review",
          "name": `${review.author}'s Review`,
          "author": {
            "@type": "Person",
            "name": review.author
          },
          "reviewBody": review.reviewBody,
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": review.ratingValue,
            "bestRating": 5,
            "worstRating": 1
          },
          ...(review.datePublished && { "datePublished": review.datePublished }),
          "itemReviewed": {
            "@id": `${BUSINESS_INFO.website}/#organization`,
            "name": BUSINESS_INFO.name
          }
        }
      }))
    } : null;

    // Article schema for blog posts
    // Build image array for Article schema
    const buildArticleImages = () => {
      if (articleSchema?.images && articleSchema.images.length > 0) {
        return articleSchema.images.map((img) => ({
          "@type": "ImageObject",
          "url": img.url,
          ...(img.caption && { "caption": img.caption }),
          ...(img.width && { "width": img.width }),
          ...(img.height && { "height": img.height })
        }));
      }
      if (articleSchema?.image) {
        return articleSchema.image;
      }
      return undefined;
    };

    const articleSchemaData = (isBlogPost || includeArticleSchema) && articleSchema ? {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": articleSchema.headline,
      "description": articleSchema.description,
      "author": articleSchema.author && articleSchema.author !== BUSINESS_INFO.name
        ? { "@type": "Person", "name": articleSchema.author, "worksFor": { "@id": `${BUSINESS_INFO.website}/#organization` } }
        : { "@id": `${BUSINESS_INFO.website}/#organization`, "name": BUSINESS_INFO.name },
      "publisher": { "@id": `${BUSINESS_INFO.website}/#organization` },
      "datePublished": articleSchema.datePublished,
      "dateModified": articleSchema.dateModified || articleSchema.datePublished,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": effectiveCanonical
      },
      ...(buildArticleImages() && { "image": buildArticleImages() })
    } : null;

    // HowTo schema for step-by-step guides
    const howToSchemaData = howToSchema ? {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": howToSchema.name,
      "description": howToSchema.description,
      ...(howToSchema.totalTime && { "totalTime": howToSchema.totalTime }),
      ...(howToSchema.estimatedCost && {
        "estimatedCost": {
          "@type": "MonetaryAmount",
          "currency": howToSchema.estimatedCost.currency,
          "value": howToSchema.estimatedCost.value
        }
      }),
      ...(howToSchema.supply && {
        "supply": howToSchema.supply.map(item => ({
          "@type": "HowToSupply",
          "name": item
        }))
      }),
      ...(howToSchema.tool && {
        "tool": howToSchema.tool.map(item => ({
          "@type": "HowToTool",
          "name": item
        }))
      }),
      "step": howToSchema.steps.map((step, index) => ({
        "@type": "HowToStep",
        "position": index + 1,
        "name": step.name,
        "text": step.text,
        ...(step.image && { "image": step.image })
      }))
    } : null;

    // Combine schemas based on page type
    const schemas: object[] = [];
    
    // For town pages, use the enhanced town-specific LocalBusiness schema
    if (isTownPage && townLocalBusinessSchema) {
      schemas.push(baseLocalBusinessSchema, townLocalBusinessSchema);
    } else if (!isBlogPost) {
      schemas.push(mergedLocalBusinessSchema);
    }
    
    // Add organization and website schema for homepage
    if (isHomePage) {
      schemas.unshift(websiteSchema);
    }
    
    // Add breadcrumb schema for town pages and service pages
    if (breadcrumbSchema) schemas.push(breadcrumbSchema);
    
    if (serviceSchemaData && !isTownPage) schemas.push(serviceSchemaData);
    if (faqSchemaData) schemas.push(faqSchemaData);
    if (articleSchemaData) schemas.push(articleSchemaData);
    if (howToSchemaData) schemas.push(howToSchemaData);
    if (itemListWithRatingSchema) schemas.push(itemListWithRatingSchema);
    schemas.push(...reviewSchemas);
    if (customBreadcrumbSchema) schemas.push(customBreadcrumbSchema);
    if (extraSchemas && extraSchemas.length > 0) schemas.push(...extraSchemas);

    // Google image metadata accepts Person/Organization creators, not a
    // LocalBusiness-only reference. Type the existing identity, never a new one.
    const typeImageCreators = (value: unknown): void => {
      if (Array.isArray(value)) { value.forEach(typeImageCreators); return; }
      if (!value || typeof value !== 'object') return;
      const record = value as Record<string, unknown>;
      if (record['@type'] === 'ImageObject' && record.creator && typeof record.creator === 'object') {
        const creator = record.creator as Record<string, unknown>;
        if (creator['@id'] === `${BUSINESS_INFO.website}/#organization`) {
          record.creator = { ...creator, '@type': 'Organization', name: BUSINESS_INFO.name };
        }
      }
      Object.values(record).forEach(typeImageCreators);
    };
    typeImageCreators(schemas);

    let script = document.querySelector('script[data-schema="structured-data"]') as HTMLScriptElement;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-schema", "structured-data");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(normalizeSchemaWebsiteUrls(schemas));

    // Cleanup function
    return () => {
      // We don't remove tags on cleanup since they should persist
    };
  }, [title, description, ogTitle, ogType, ogDescription, breadcrumbItems, extraSchemas, canonicalUrl, townName, countyName, ogImage, ogImageAlt, serviceSchema, faqItems, reviews, articleSchema, howToSchema, localBusinessSchema, isHomePage, isServicePage, isBlogPost, includeArticleSchema, isTownPage, serviceName, noIndex]);

  return null;
};

export default SEOHead;
